import { useEffect, useState } from 'react';
import type { AttendanceState } from '../home/homeModel';

/**
 * Minutes worked today, live: the server's `today_minutes` (as of the fetch)
 * plus the minutes since, while on shift. Breaks are not counted. Shared by
 * the top-bar attendance control and the Home status line.
 */
export function useWorkedMinutes(state: AttendanceState, todayMinutes: number, fetchedAt: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (state !== 'clocked_in') return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [state, fetchedAt]);
  if (state !== 'clocked_in' || !fetchedAt) return todayMinutes;
  return todayMinutes + Math.max(0, Math.floor((now - fetchedAt) / 60_000));
}

/**
 * FIX-DOCUMENTED-LIVE — pure: the documented minutes now = the server's number
 * (closed logs + running timers as of the fetch) + the minutes since the fetch
 * for each running timer. A stop/start refetches the status, so nothing is
 * added twice.
 */
export function liveDocumentedMinutes(documented: number, runningTimers: number, fetchedAt: number, now: number): number {
  if (!runningTimers || !fetchedAt) return documented;
  return documented + runningTimers * Math.max(0, Math.floor((now - fetchedAt) / 60_000));
}

/** Documented minutes today, ticking while a timer runs (no extra polling: the same 30s tick as the worked time). */
export function useLiveDocumentedMinutes(documented: number | undefined, runningTimers: number, fetchedAt: number): number | undefined {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!runningTimers) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [runningTimers, fetchedAt]);
  return typeof documented === 'number' ? liveDocumentedMinutes(documented, runningTimers, fetchedAt, now) : undefined;
}

/** D-336 — elapsed time of the current break from its `since`: "0:25" (m:ss), "1:02:09" from an hour. */
export function formatBreakElapsed(sinceIso: string | undefined, now: number): string {
  const t = sinceIso ? new Date(sinceIso).getTime() : NaN;
  const total = Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 1000)) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** D-336 — ticks every second while on a break; returns the running break time. */
export function useBreakElapsed(active: boolean, sinceIso: string | undefined): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active, sinceIso]);
  return formatBreakElapsed(sinceIso, now);
}
